import type {
  ButtonHTMLAttributes,
  ComponentPropsWithoutRef,
  ReactNode,
} from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";

export type SelectionListAlign = "center" | "start";

const alignments: Record<SelectionListAlign, string> = {
  center: "items-center",
  start: "items-start",
};

export type SelectionListProps = ComponentPropsWithoutRef<"div"> & {
  children?: ReactNode;
};

export function SelectionList({
  children,
  className,
  role = "list",
  ...rest
}: SelectionListProps) {
  return (
    <div role={role} className={cn("space-y-2", className)} {...rest}>
      {children}
    </div>
  );
}

export type SelectionListItemProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "title"
> & {
  title: ReactNode;
  secondary?: ReactNode;
  leading?: ReactNode;
  leadingIcon?: ReactNode;
  trailing?: ReactNode;
  selected?: boolean;
  align?: SelectionListAlign;
  truncate?: boolean;
};

export function SelectionListItem({
  title,
  secondary,
  leading,
  leadingIcon,
  trailing,
  selected = false,
  align = "center",
  truncate = true,
  className,
  type = "button",
  disabled,
  "aria-current": ariaCurrent,
  ...buttonProps
}: SelectionListItemProps) {
  const resolvedLeading = leading ?? leadingIcon;
  const isLegacyLeadingIcon = leading == null && leadingIcon != null;

  return (
    <div role="listitem">
      <button
        {...buttonProps}
        type={type}
        disabled={disabled}
        aria-current={selected ? (ariaCurrent ?? "true") : ariaCurrent}
        className={cn(
          "flex w-full justify-between gap-3 rounded-md py-3 pl-3.5 pr-3 text-left text-text transition-[background-color,color] duration-base ease-standard motion-reduce:transition-none",
          alignments[align],
          selected ? "bg-fill" : "hover:bg-fill",
          disabled && "cursor-not-allowed opacity-60",
          focusRing,
          className,
        )}
      >
        <span className={cn("flex min-w-0 gap-3", alignments[align])}>
          {resolvedLeading != null && (
            <span
              className="text-text-tertiary flex shrink-0 items-center"
              aria-hidden={isLegacyLeadingIcon ? "true" : undefined}
            >
              {resolvedLeading}
            </span>
          )}
          <span className="flex min-w-0 flex-col">
            <span
              className={cn(
                "text-body text-text",
                truncate ? "truncate" : "min-w-0",
              )}
            >
              {title}
            </span>
            {secondary != null && (
              <span
                className={cn(
                  "text-caption text-text-tertiary",
                  truncate ? "truncate" : "min-w-0",
                )}
              >
                {secondary}
              </span>
            )}
          </span>
        </span>
        {trailing != null && <span className="shrink-0">{trailing}</span>}
      </button>
    </div>
  );
}
